import React, { useState } from 'react';
import get from 'lodash/get';
import partition from 'lodash/partition';
import Avatar from '@atlaskit/avatar';
import { Flex } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { VerticalDivider, useFeatureToggle } from '@css-xp/base';
// @ts-ignore
import UserIcon from '@css-xp/base/assets/img/application/icon-user.svg';
import { Maybe, EndCustomerContact } from '@css-xp/graphtypes/src/generated-types-new';
import { NoData } from '@css-xp/ppc-common';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { CustomerContactsAvatarGroup } from '@css-xp/ppc-subscription-table';
import { SkeletonLoader, SkeletonLoaderWrapper, SkeletonLoaderProps } from '@css-xp/skeleton-loader';
import { useFetchEccFromContactService } from './use-fetch-ecc-contact-service';
import { ContactType } from './constant';

export interface EndCustomerContactProps {
  invoiceGroupId: string;
  transactionAccountId: string;
  returnAsSummaryContainer?: boolean;
  contactType?: ContactType;
  version?: number;
  loaderOptions?: SkeletonLoaderProps;
  timestampComparator?: (_timestamp: Maybe<string> | undefined) => boolean;
  noDataClassName?: string;
  shouldHidePrimaryContact?: boolean;
  shouldRenderForInternalUse?: boolean;
}

const getSkeletonLoader = (loaderOptions = {}, version?: number) => {
  if (version === 2) {
    return (
      <SkeletonLoaderWrapper>
        <Flex>
          <SkeletonLoader circle={true} height="32px" count={5} marginRight="-1px" marginLeft="0" {...loaderOptions} />
        </Flex>
      </SkeletonLoaderWrapper>
    );
  }

  return (
    <SkeletonLoaderWrapper>
      <SkeletonLoader width="60%" marginBottom="0" {...loaderOptions} />
    </SkeletonLoaderWrapper>
  );
};

export const EndCustomerContacts = ({
  invoiceGroupId,
  transactionAccountId,
  loaderOptions,
  version,
  contactType,
  timestampComparator,
  noDataClassName,
  shouldHidePrimaryContact,
  shouldRenderForInternalUse,
}: EndCustomerContactProps) => {
  const { getECCDetails, eccDetailsLoading } = useFetchEccFromContactService(contactType);
  const [eccDetails, setEccDetails] = useState([]);

  const [isEnabled] = useFeatureToggle();

  const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);

  const filterCustomerContactsByTimestamp = (contacts: EndCustomerContact[]): EndCustomerContact[] => {
    return contacts.filter((contact: EndCustomerContact) =>
      timestampComparator && contact.updatedAt ? timestampComparator(contact.updatedAt) : true
    );
  };

  React.useEffect(() => {
    (async () => {
      const data = await getECCDetails(invoiceGroupId, transactionAccountId);
      setEccDetails(data);
    })();
  }, [invoiceGroupId, transactionAccountId]);

  const primaryContactLabel = shouldRenderForInternalUse ? 'Key contact' : undefined;
  const secondaryContactLabel = shouldRenderForInternalUse ? 'Other contact' : undefined;

  if (eccDetailsLoading) {
    return getSkeletonLoader(loaderOptions, version);
  } else if ((eccDetails || []).length === 0) {
    return <NoData className={noDataClassName} />;
  } else if (!isCustomerContactV3Enabled || shouldHidePrimaryContact) {
    return (
      <CustomerContactsAvatarGroup
        contacts={filterCustomerContactsByTimestamp(eccDetails)}
        primaryLabel={primaryContactLabel}
        secondaryLabel={secondaryContactLabel}
      />
    );
  } else {
    const [primaryCustomerContacts, secondaryCustomerContacts] = partition(eccDetails, (contact: EndCustomerContact) =>
      get(contact, 'isPrimary', false)
    ).map((contacts) => filterCustomerContactsByTimestamp(contacts));

    return (
      <Flex>
        {primaryCustomerContacts.length > 0 ? (
          <CustomerContactsAvatarGroup
            contacts={primaryCustomerContacts}
            showContactType={true}
            primaryLabel={primaryContactLabel}
            secondaryLabel={secondaryContactLabel}
          />
        ) : (
          <Tooltip content={shouldRenderForInternalUse ? 'Unassigned (Key contact)' : 'Unassigned (Primary)'}>
            <Avatar src={UserIcon.toString()} testId="unassigned-primary-contact"></Avatar>
          </Tooltip>
        )}
        {secondaryCustomerContacts.length > 0 && (
          <>
            <VerticalDivider />
            <CustomerContactsAvatarGroup
              contacts={secondaryCustomerContacts}
              showContactType={true}
              primaryLabel={primaryContactLabel}
              secondaryLabel={secondaryContactLabel}
            />
          </>
        )}
      </Flex>
    );
  }
};
